import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import api from '../services/api'; // Utiliser le service API
import DOMPurify from 'dompurify';
import { getSupabase } from '../supabaseClient'; // Importer getSupabase
import { toast } from 'react-hot-toast'; // Importer toast de react-hot-toast
import { useSingletonFetch } from '../hooks/useSingletonFetch';

// Fonction pour tronquer le texte HTML
const truncateHtml = (html, maxLength = 200) => {
  if (!html) return '';
  
  // Créer un élément temporaire pour extraire le texte
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = DOMPurify.sanitize(html);
  let text = tempDiv.textContent || tempDiv.innerText || '';
  
  // Tronquer le texte
  if (text.length > maxLength) {
    text = text.substr(0, maxLength) + '...';
  }
  
  return text;
};

const SearchResults = () => {
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // Fonction pour forcer le téléchargement du formulaire
  const handleDownloadForm = async (formUrl) => {
    try {
      // Utiliser fetch pour télécharger le fichier car il s'agit d'une URL externe
      const response = await fetch(formUrl);
      const blob = await response.blob();

      // Extraire le nom de fichier de l'URL
      const filename = formUrl.substring(formUrl.lastIndexOf('/') + 1);
      
      // Créer un lien temporaire pour le téléchargement
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      
      // Nettoyer
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        //console.error('Erreur lors du téléchargement du formulaire:', error);
      } else {
        //console.error('Erreur lors du téléchargement du formulaire');
      }
      // Optionnel : afficher un message d'erreur à l'utilisateur
    }
  };

  const location = useLocation();
  const navigate = useNavigate();
  
  // Utilisation de useMemo pour stabiliser queryParams
  const queryParams = useMemo(() => {
    return new URLSearchParams(location.search);
  }, [location.search]);

  const searchQuery = queryParams.get('q') || '';
  const categoryId = queryParams.get('category') || '';
  
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    category: categoryId
  });
  // Référence pour éviter les doubles appels
  const searchPerformedRef = React.useRef(false);

  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);

  // Utiliser le hook useSingletonFetch pour éviter les doubles requêtes
  const fetchServicesAndCategories = useCallback(async () => {
    try {
      const supabase = getSupabase();
      
      // Charger tous les services actifs
      const { data: servicesData, error: servicesError } = await supabase
        .from('services')
        .select('*, categories!inner(id, name)')
        .eq('active', true)
        .order('name');
      
      if (servicesError) throw servicesError;
      
      // Charger les catégories actives
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('categories')
        .select('*, services!inner(id, name)')
        .eq('active', true)
        .order('name');
      
      if (categoriesError) throw categoriesError;

      // Sélection automatique du service si présent dans l'URL
      const serviceId = queryParams.get('service');
      if (serviceId) {
        const selectedService = servicesData.find(s => s.id === serviceId);
        if (selectedService) {
          setFilters(prev => ({
            ...prev,
            service: serviceId
          }));
        }
      }

      // Sélection automatique de la catégorie si présent dans l'URL
      const categoryId = queryParams.get('category');
      if (categoryId) {
        const selectedCategory = categoriesData.find(c => c.id === categoryId);
        if (selectedCategory) {
          // Si aucun service n'est sélectionné, trouver et sélectionner le service parent
          const serviceId = queryParams.get('service');
          if (!serviceId) {
            // Trouver le premier service associé à cette catégorie
            const parentService = servicesData.find(service => 
              service.categories && 
              service.categories.some(cat => cat.id === categoryId)
            );
            
            if (parentService) {
              setFilters(prev => ({
                ...prev,
                category: categoryId,
                service: parentService.id
              }));
            } else {
              setFilters(prev => ({
                ...prev,
                category: categoryId
              }));
            }
          } else {
            setFilters(prev => ({
              ...prev,
              category: categoryId
            }));
          }
        }
      }

      setServices(servicesData || []);
      setCategories(categoriesData || []);
      return { servicesData, categoriesData };

    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        //console.error('❌ Erreur de chargement:', err);
      } else {
        //console.error('❌ Erreur de chargement');
      }
      toast.error('Impossible de charger les services et catégories');
      throw err;
    }
  }, [queryParams]);

  // Utiliser useSingletonFetch pour éviter les doubles appels
  useSingletonFetch(
    'fetch-services-categories',
    fetchServicesAndCategories,
    [queryParams]
  );

  // Fonction pour récupérer les résultats de recherche
  const fetchResults = useCallback(async () => {
    // Éviter les doubles appels
    if (searchPerformedRef.current) {
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      searchPerformedRef.current = true;
      
      // Vérifier si les paramètres de recherche sont vides
      const hasSearchParams = 
        searchQuery || 
        filters.service || 
        filters.category;

      // Préparer les paramètres de recherche
      const searchParams = {
        query: searchQuery || '',
        category: filters.category || queryParams.get('category') || '',
        service: filters.service || queryParams.get('service') || '',
        fetchAll: !hasSearchParams,  // Nouveau paramètre pour charger tous les actes
        activeOnly: true  // Nouveau paramètre pour ne charger que les actes actifs
      };

      const response = await api.searchConsularActsWithParams(searchParams);

      // Mise à jour des résultats
      const fetchedResults = response.data.data || [];
      
      // Filtrer les actes consulaires inactifs
      const activeResults = fetchedResults.filter(act => act.active !== false);

      setResults(activeResults);
      setLoading(false);
      
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        //console.error('❌ Erreur de recherche:', err);
      } else {
        //console.error('❌ Erreur de recherche');
      }
      searchPerformedRef.current = false; // Réinitialiser pour permettre une nouvelle tentative
      
      // Gestion des erreurs axios
      if (err.response) {
        // La requête a été faite et le serveur a répondu avec un code d'état
        // qui ne se trouve pas dans la plage 2xx
        const errorMessage = err.response.data.message || 'Erreur lors de la recherche';
        setError(errorMessage);
        toast.error(errorMessage);
      } else if (err.request) {
        // La requête a été faite mais aucune réponse n'a été reçue
        setError('Pas de réponse du serveur');
        toast.error('Impossible de joindre le serveur');
      } else {
        // Quelque chose s\'est passé lors de la configuration de la requête
        setError('Erreur de configuration de la requête');
        toast.error('Erreur lors de la préparation de la recherche');
      }
      
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, filters, queryParams]);

  // Relancer la recherche quand les filtres changent
  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  // Utilisation du hook de fetch singleton
  const { 
    data: searchResults, 
    error: searchError 
  } = useSingletonFetch(
    'search-results', // Clé unique pour cette requête
    fetchResults, 
    [
      searchQuery, 
      filters.service,
      filters.category, 
      queryParams
    ]
  );

  // Mettre à jour les états locaux quand les données changent
  useEffect(() => {

    if (searchResults) {
      setResults(searchResults);
    }
    if (searchError) {
      setError(searchError);
    }
    setLoading(false);
  }, [searchResults, searchError]);

  // Gestion de l'affichage des erreurs
  if (error) {
    return (
      <div className="p-4 bg-red-100 text-red-800 rounded-md">
        <p>Erreur : {typeof error === 'string' ? error : 'Une erreur est survenue'}</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6 space-y-4 md:space-y-0">
        <h1 className="text-2xl font-bold text-gray-900 w-full md:w-auto">
          {searchQuery 
            ? `Résultats pour '${searchQuery}'` 
            : categoryId 
              ? 'Résultats par catégorie' 
              : 'Tous les actes consulaires'}
        </h1>
        
        {/* Search Bar */}
        <form 
          className="w-full md:w-auto flex items-center" 
          onSubmit={(e) => {
            e.preventDefault();
            const formData = new FormData(e.target);
            const query = formData.get('query');
            
            // Réinitialiser la référence pour permettre une nouvelle recherche
            searchPerformedRef.current = false;
            
            // Conserver les autres paramètres existants
            const newSearchParams = new URLSearchParams(location.search);
            newSearchParams.set('q', query);
            
            // Naviguer avec les nouveaux paramètres
            navigate(`${location.pathname}?${newSearchParams.toString()}`, { replace: true });
            
            // Forcer un rafraîchissement des résultats
            setTimeout(() => fetchResults(), 0);
          }}>
          <div className="relative w-full md:w-64 mr-0 md:mr-4">
            <input
              type="text"
              name="query"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              placeholder="Affiner votre recherche"
              defaultValue={searchQuery}
            />
            <button 
              type="submit" 
              className="absolute right-0 top-0 bottom-0 px-3 flex items-center justify-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </button>
          </div>
        </form>
      </div>
      
      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm mb-6">
        <div className="flex flex-col md:flex-row md:flex-wrap gap-4">
          {/* Sélection du service */}
          <div className="mb-4 w-full md:w-auto md:flex-1">
            <label 
              htmlFor="service-select" 
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Service
            </label>
            <select
              id="service-select"
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
              value={filters.service || ''}
              onChange={(e) => {
                const selectedServiceId = e.target.value;
                // Suppression de la variable non utilisée pour résoudre l'erreur ESLint
                
                //console.log('🔍 Service sélectionné:', {
                //  serviceId: selectedServiceId,
                //  serviceName: services.find(service => service.id === selectedServiceId)?.name
                //});

                // Réinitialiser la référence pour permettre une nouvelle recherche
                searchPerformedRef.current = false;

                // Mettre à jour les filtres
                setFilters(prev => ({
                  ...prev,
                  service: selectedServiceId,
                  category: null  // Réinitialiser la catégorie
                }));
                
                // Mettre à jour l'URL
                const newSearchParams = new URLSearchParams(location.search);
                newSearchParams.set('service', selectedServiceId);
                newSearchParams.delete('category');
                
                // Naviguer avec les nouveaux paramètres
                navigate(`${location.pathname}?${newSearchParams.toString()}`);
              }}
            >
              <option value="">Sélectionner un service</option>
              {services.map(service => (
                <option 
                  key={service.id} 
                  value={service.id}
                >
                  {service.name}
                </option>
              ))}
            </select>
          </div>
          
          {/* Sélection de la catégorie */}
          <div className="mb-4 w-full md:w-auto md:flex-1">
            <label 
              htmlFor="category-select" 
              className={`block text-sm font-medium ${!filters.service ? 'text-gray-400' : 'text-gray-700'} mb-2`}
            >
              Catégorie {!filters.service && '(sélectionnez d\'abord un service)'}
            </label>
            <select
              id="category-select"
              className={`block w-full px-3 py-2 border ${!filters.service ? 'bg-gray-100 cursor-not-allowed' : ''} border-gray-300 rounded-md shadow-sm focus:outline-none ${filters.service ? 'focus:ring-primary focus:border-primary' : ''}`}
              value={filters.category || ''}
              disabled={!filters.service}
              onChange={(e) => {
                const selectedCategoryId = e.target.value;
                // Suppression de la variable non utilisée pour résoudre l'erreur ESLint
                
                //console.log('🔍 Catégorie sélectionnée:', {
                //  categoryId: selectedCategoryId,
                //  categoryName: categories.find(category => category.id === selectedCategoryId)?.name
                //});

                // Réinitialiser la référence pour permettre une nouvelle recherche
                searchPerformedRef.current = false;

                // Mettre à jour les filtres
                setFilters(prev => ({
                  ...prev,
                  category: selectedCategoryId
                }));
                
                // Mettre à jour l'URL
                const newSearchParams = new URLSearchParams(location.search);
                newSearchParams.set('category', selectedCategoryId);
                
                // Naviguer avec les nouveaux paramètres
                navigate(`${location.pathname}?${newSearchParams.toString()}`);
              }}
            >
              <option value="">Sélectionner une catégorie</option>
              {categories
                .filter(category => 
                  !filters.service || 
                  category.service_id === filters.service
                )
                .map(category => (
                  <option 
                    key={category.id} 
                    value={category.id}
                  >
                    {category.name}
                  </option>
                ))
              }
            </select>
          </div>
          
          <button 
            className="text-primary hover:text-primary/80 font-medium text-sm"
            onClick={() => {
              setFilters({
                category: ''
              });
              setServices([]);
            }}
          >
            Réinitialiser les filtres
          </button>
        </div>
      </div>
      
      {/* Results */}
      <div className="space-y-6">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <svg className="animate-spin h-10 w-10 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
        ) : error ? (
          <div className="bg-white p-6 rounded-lg shadow-sm text-center">
            <p className="text-lg text-gray-600">{error}</p>
          </div>
        ) : results.length === 0 ? (
          <div className="bg-white p-6 rounded-lg shadow-sm text-center">
            <p className="text-lg text-gray-600">Aucun résultat trouvé. Veuillez essayer une autre recherche ou modifier vos filtres.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {results.map((result) => (
              <div 
                key={result.id} 
                className="bg-white shadow-md rounded-lg p-6 flex flex-col"
              >
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold text-gray-900">{result.title}</h2>
                  <Link 
                    to={`/actes/${result.id}`}
                    className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary whitespace-nowrap"
                  >
                    Voir les détails
                  </Link>
                </div>

                <div className="flex-grow">
                  {result.description && (
                    <p className="text-gray-600 mb-4">{truncateHtml(result.description)}</p>
                  )}
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    {result.processing_time && (
                      <div className="bg-gray-100 p-3 rounded-md">
                        <p className="text-sm font-medium text-gray-700">Délai de traitement</p>
                        <p className="text-lg font-bold text-gray-900">{result.processing_time}</p>
                      </div>
                    )}
                    
                    {typeof result.processing_fee === 'number' && result.processing_fee > 0 && (
                      <div className="bg-gray-100 p-3 rounded-md">
                        <p className="text-sm font-medium text-gray-700">
                          {result.title.toLowerCase().includes('passeport') ? 'Frais de passeport + RDV' : result.title.toLowerCase().includes('visa') ? 'Frais de visa' : result.title === 'Carte nationale d\'identité' ? 'Frais de timbre' : 'Droits de chancellerie'}
                        </p>
                        <p className="text-lg font-bold text-gray-900">{`${result.processing_fee} €`}</p>
                      </div>
                    )}

                    {result.shipping_fees && Array.isArray(result.shipping_fees) && result.shipping_fees.length > 0 && result.title !== 'Carte consulaire' && result.title !== 'Carte nationale d\'identité' && !result.title.toLowerCase().includes('visa') && (
                      <div className="bg-gray-100 p-3 rounded-md">
                        <p className="text-sm font-bold text-gray-700 mb-1">Frais d&apos;expédition</p>
                        <ul className="text-gray-900 text-sm space-y-1">
                          {result.shipping_fees.map((zoneFee, idx) => {
                            const zone = Object.keys(zoneFee)[0];
                            const fee = zoneFee[zone];
                            return fee && fee !== "" && fee > 0 ? (
                              <li key={idx} className="flex justify-between">
                                <span>{zone}</span>
                                <span className="font-bold">{fee} €</span>
                              </li>
                            ) : null;
                          })}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-4">
                  {result.form_url && 
                   !result.title.toLowerCase().includes('passeport') && 
                   !result.title.toLowerCase().includes('visa') && 
                   result.title !== 'Carte nationale d\'identité' && (
                    <button 
                      onClick={() => handleDownloadForm(result.form_url)}
                      className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="-ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Télécharger le formulaire
                    </button>
                  )}
                  
                  {result.title !== 'Carte consulaire' && result.title !== 'Carte nationale d\'identité' && result.title.toLowerCase().includes('passeport') && typeof result.processing_fee === 'number' && result.processing_fee > 0 && (
                    <>
                      <Link 
                        to={`/payment?ser=86ca8f54-4368-4058-ab64-359aff93ea6c&cat=1d4877b2-385a-4288-9b61-d6e6f85b1376&act=1c34d0de-38f7-4bc6-91cc-88000ff61f42`}
                        className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="-ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                        </svg>
                        Payer les frais d&apos;expédition
                      </Link>
                      <a
                        href="https://www.monpasseport.ci/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="-ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                        Faire la demande de passeport
                      </a>
                    </>
                  )}
                  
                  {result.title !== 'Carte consulaire' && result.title.toLowerCase().includes('visa') && typeof result.processing_fee === 'number' && result.processing_fee > 0 && (
                    <>
                      <a
                        href="https://snedai.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="-ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                        Payer les frais de visa
                      </a>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchResults;
